/**
 * One side's clock face (design L4/L5: Thunder, tabular, 38 px).
 *
 * The quiet face is a `raised` tile with the theme's top highlight edge; the
 * active face is the bronze card with its glow; low time swaps the bronze for
 * danger red (the glow stays, so the face keeps its weight on the row).
 *
 * `tokens.lowTimeFilled` (M1b) is the CircleChess artboard's louder rule: a low
 * clock is a SOLID danger block with `onDanger` digits whether or not it is the
 * side to move. The Gallery palettes keep the M1a behaviour.
 *
 * TWO clean-up changes of 2026-10-05:
 *  - the pill is 72x40, right-aligned in the 56 px player row (R6.3), where M1a
 *    drew a 92x44 slab;
 *  - THE DIGITS ARE OPTICALLY CENTRED (owner item 3). `Thunder` is a condensed
 *    display face whose digits sit high in its em box, so `TYPE.clock`'s 40 px
 *    leading inside a 40 px pill put them a couple of pixels above centre on
 *    every theme. The fix is here and not in `TYPE.clock`: the leading has to
 *    equal THE PILL's inner height, which is this View's business, and
 *    `theme/tokens.ts` is the shared core (`src/Store/liveGame/SYNC.md`).
 */
import React from 'react';
import {StyleSheet, View} from 'react-native';

import type {Side} from '@/Store/liveGame/state';

import {RADIUS} from '../../theme/tokens';
import {useV2Theme} from '../../theme/useV2Theme';
import {Txt} from '../../ui';
import {ClockModel} from './model';

/** R6.3: the pill, right-aligned in the player row */
const PILL_HEIGHT = 40;
const PILL_MIN_WIDTH = 72;
/** the digits' leading IS the pill's inner height — nothing left to centre */
const DIGIT_LINE_HEIGHT = PILL_HEIGHT;
/** Thunder's digits sit ~2 px high in its em box; nudge them back down */
const DIGIT_OPTICAL_Y = 2;

export type ClockViewProps = ClockModel & {side: Side};

export default React.memo(function ClockView({
  text,
  low,
  active,
  side,
}: ClockViewProps) {
  const tokens = useV2Theme(s => s.tokens);

  const filled = active || (low && tokens.lowTimeFilled);
  const face = filled
    ? {
        backgroundColor: low ? tokens.danger : tokens.clock,
        boxShadow: `0 10px 26px -8px ${tokens.clockGlow}`,
      }
    : {
        backgroundColor: tokens.raised,
        boxShadow: `inset 0 0 0 1px ${tokens.line}, ${tokens.hiEdge}`,
      };

  return (
    <View testID={`v2-clock-${side}`} style={[styles.face, face as any]}>
      <Txt
        variant="clock"
        tone={
          low && tokens.lowTimeFilled
            ? 'onDanger'
            : filled
            ? 'onClock'
            : 'muted'
        }
        numeric
        style={styles.digits}
        // the clock is the one thing a screen reader should NOT re-announce
        accessibilityLiveRegion="none">
        {text}
      </Txt>
    </View>
  );
});

const styles = StyleSheet.create({
  face: {
    height: PILL_HEIGHT,
    minWidth: PILL_MIN_WIDTH,
    paddingHorizontal: 10,
    borderRadius: RADIUS.control,
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  digits: {
    lineHeight: DIGIT_LINE_HEIGHT,
    transform: [{translateY: DIGIT_OPTICAL_Y}],
  },
});
